// Mirrors server/services/breakScore.js so projected scores can be computed
// live as the user types spot prices.
export function computeBreakScore({ totalSpots, soldPrices, cogs }) {
  totalSpots = Number(totalSpots) || 0;
  const prices = (soldPrices || []).filter(p => p != null && !isNaN(p) && Number(p) > 0).map(Number);

  if (prices.length === 0) return null;
  if (cogs == null || Number(cogs) <= 0) return null;
  if (totalSpots <= 0) return null;

  const avgSold = prices.reduce((s, p) => s + p, 0) / prices.length;
  const projectedSales = avgSold * totalSpots;
  if (projectedSales <= 0) return null;

  const profit = projectedSales - Number(cogs);
  const marginPct = (profit / projectedSales) * 100;

  let marginScore;
  if (marginPct <= 40) {
    marginScore = (marginPct / 40) * 85;
    if (marginScore < 0) marginScore = 0;
    if (marginScore > 85) marginScore = 85;
  } else {
    marginScore = 85 + ((marginPct - 40) / 20) * 15;
    if (marginScore > 100) marginScore = 100;
  }

  let revenueScore = (projectedSales / 5000) * 15;
  if (revenueScore < 0) revenueScore = 0;
  if (revenueScore > 15) revenueScore = 15;

  let score = Math.round(marginScore + revenueScore);
  if (score < 1) score = 1;
  if (score > 100) score = 100;

  return {
    score,
    marginScore: Math.round(marginScore * 10) / 10,
    revenueScore: Math.round(revenueScore * 10) / 10,
    projectedSales,
    marginPct,
  };
}

// Inverse of computeBreakScore by bisection. Given totalSpots + cogs, return
// the avg price per spot needed to hit `targetScore`, or null when unreachable
// or the inputs are invalid. Reuses computeBreakScore so the inverse can't
// drift from the forward function.
export function avgPriceForScore(targetScore, totalSpots, cogs) {
  totalSpots = Number(totalSpots) || 0;
  cogs = Number(cogs) || 0;
  if (totalSpots <= 0 || cogs <= 0) return null;
  if (!(targetScore >= 1 && targetScore <= 100)) return null;
  const scoreOf = (avg) => {
    const r = computeBreakScore({ totalSpots, soldPrices: [avg], cogs });
    return r?.score ?? 0;
  };
  let lo = 0, hi = 100000;
  if (scoreOf(hi) < targetScore) return null;
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    if (scoreOf(mid) < targetScore) lo = mid;
    else hi = mid;
  }
  return hi;
}

// 0-19 red, 20-39 orange, 40-59 yellow, 60-79 green, 80-100 dark green
export function scoreColor(score) {
  if (score == null) return 'var(--text-light)';
  if (score <= 19) return '#dc2626';
  if (score <= 39) return '#ea580c';
  if (score <= 59) return '#ca8a04';
  if (score <= 79) return '#16a34a';
  return '#15803d';
}

// Background tint for badges — paired with scoreColor for the text.
export function scoreBg(score) {
  if (score == null) return 'transparent';
  if (score <= 19) return '#fee2e2';
  if (score <= 39) return '#ffedd5';
  if (score <= 59) return '#fef9c3';
  if (score <= 79) return '#dcfce7';
  return '#bbf7d0';
}
